import './polyfills';
import 'intersection-observer';
import cssVars from 'css-vars-ponyfill';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { DeviceProvider } from './contexts/DeviceContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { WatchProgressProvider } from './contexts/WatchProgressContext';
import { WatchHistoryProvider } from './contexts/WatchHistoryContext';
import { LiveHistoryProvider } from './contexts/LiveHistoryContext';
import App from './App';
import { registerTizenRemoteKeys } from './utils/tizenKeys';
import { installRemoteInputDigits } from './utils/remoteInput';
import { PrivacyConsent } from './pages/PrivacyConsent/PrivacyConsent';
import { hasPrivacyConsent } from './utils/privacyConsent';

// Register Tizen media remote keys before any event listeners are attached
registerTizenRemoteKeys();
installRemoteInputDigits();

// Apply css-vars-ponyfill only on browsers without native CSS Custom Properties support (Chrome <49)
if (!(window.CSS && (CSS as any).supports && (CSS as any).supports('--a', '0'))) {
  cssVars({ onlyLegacy: true, watch: true });
}

// Inject capability class flags used by legacy CSS fallbacks
if (!(window.CSS && (CSS as any).supports && (CSS as any).supports('display', 'grid'))) {
  document.documentElement.classList.add('no-grid');
}

(function supportsFlexGap() {
  try {
    const flex = document.createElement('div');
    flex.style.display = 'flex';
    flex.style.flexDirection = 'column';
    flex.style.rowGap = '1px';
    const c1 = document.createElement('div');
    const c2 = document.createElement('div');
    c1.style.height = '0';
    c2.style.height = '0';
    flex.appendChild(c1);
    flex.appendChild(c2);
    document.body.appendChild(flex);
    const ok = flex.scrollHeight === 1;
    document.body.removeChild(flex);
    if (!ok) document.documentElement.classList.add('no-flex-gap');
  } catch { document.documentElement.classList.add('no-flex-gap'); }
})();


// Prevent the browser from navigating back on Backspace.
// Allow Backspace only when focus is inside an editable element.
document.addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key !== 'Backspace') return;
  const el = e.target as HTMLElement;
  const editable =
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.isContentEditable;
  if (!editable) e.preventDefault();
}, { capture: true });

function ConsentGate() {
  const [consented, setConsented] = React.useState(() => hasPrivacyConsent());

  if (!consented) {
    return <PrivacyConsent onAccept={() => setConsented(true)} />;
  }

  return (
    <AuthProvider>
      <DeviceProvider>
        <FavoritesProvider>
          <WatchProgressProvider>
            <WatchHistoryProvider>
              <LiveHistoryProvider>
                <App />
              </LiveHistoryProvider>
            </WatchHistoryProvider>
          </WatchProgressProvider>
        </FavoritesProvider>
      </DeviceProvider>
    </AuthProvider>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <ConsentGate />
);
